import React, { useMemo, useState } from 'react';
import { Button } from '@lendi-ui/button';
import getColour, { ValidHex } from '@lendi-ui/commons/colours';
import ChatBubble from '@lendi-ui/icon/ChatBubble';
import Modal from '@lendi-ui/modal';

import {
  TableCommentsButton,
  TableCommentsIcon,
  TableContainer,
  TableTitle,
  TableTitleText,
  TableWrapper,
} from './style';

const COMMENTS_BUTTON_MIN_WIDTH = 150;

const renderCommentsButton = (onClick: () => void, commentsButtonColour: ValidHex) => (
  <TableCommentsButton type="button" color={commentsButtonColour} onClick={onClick}>
    View comments
    <TableCommentsIcon>
      <ChatBubble />
    </TableCommentsIcon>
  </TableCommentsButton>
);

interface TableProps {
  children?: React.ReactNode;
  title?: string;
  headers: {
    content: string | React.ReactNode;
    minWidth: number;
  }[];
  commentsButtonPosition?: 'title' | 'column';
  commentsModalTitle?: string;
  commentsModalContent?: React.ReactNode;
  renderCommentsModalFooter?: (props: { closeModal: () => void }) => React.ReactNode;
  footer?: React.ReactNode;
  isSticky?: boolean;
}

export const Table = (props: TableProps) => {
  const {
    children,
    title,
    headers,
    commentsButtonPosition,
    commentsModalTitle,
    commentsModalContent,
    renderCommentsModalFooter,
    footer,
    isSticky = false,
  } = props;
  const [isModalVisible, setIsModalVisible] = useState(false);

  const tableMinWidth = useMemo(() => {
    let minWidth = headers.reduce((acc, curr) => acc + curr.minWidth, 0);
    if (commentsButtonPosition === 'column') {
      minWidth += COMMENTS_BUTTON_MIN_WIDTH;
    }
    return minWidth;
  }, [headers, commentsButtonPosition]);

  const headerSizes = useMemo(() => {
    let sizes = headers.map((header) => ({
      widthRatio: header.minWidth / tableMinWidth,
      minWidth: header.minWidth,
    }));
    if (commentsButtonPosition === 'column') {
      sizes.push({
        widthRatio: COMMENTS_BUTTON_MIN_WIDTH / tableMinWidth,
        minWidth: COMMENTS_BUTTON_MIN_WIDTH,
      });
    }
    return sizes;
  }, [headers, tableMinWidth, commentsButtonPosition]);

  return (
    <TableWrapper>
      {title && (
        <TableTitle>
          <TableTitleText>{props.title}</TableTitleText>
          {commentsButtonPosition === 'title' &&
            renderCommentsButton(() => setIsModalVisible(true), getColour('interaction', 'subtle'))}
        </TableTitle>
      )}
      <TableContainer minWidth={tableMinWidth} headerSizes={headerSizes} isSticky={isSticky}>
        <table data-component="table">
          <thead>
            <tr>
              {headers.map((header, index) => (
                <th key={index} data-component="table-header">
                  {header.content}
                </th>
              ))}
              {commentsButtonPosition === 'column' && (
                <th data-component="table-header" className="right-aligned">
                  {renderCommentsButton(() => setIsModalVisible(true), getColour('interaction', 'main'))}
                </th>
              )}
            </tr>
          </thead>
          <tbody>{children}</tbody>
          {footer && (
            <tfoot>
              <tr>
                <td colSpan={headers.length + Number(commentsButtonPosition === 'column')}>{footer}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </TableContainer>
      {!!commentsButtonPosition && commentsModalTitle && commentsModalContent && (
        <Modal role="dialog" isVisible={isModalVisible} size="lg" onHide={() => setIsModalVisible(false)} isHeaderFixed>
          <Modal.Header title={commentsModalTitle} size="xs" />
          <Modal.Content>{commentsModalContent}</Modal.Content>
          <Modal.Footer>
            {renderCommentsModalFooter ? (
              renderCommentsModalFooter({ closeModal: () => setIsModalVisible(false) })
            ) : (
              <Button variant="primary" isFullWidth onClick={() => setIsModalVisible(false)}>
                Done
              </Button>
            )}
          </Modal.Footer>
        </Modal>
      )}
    </TableWrapper>
  );
};
